import { ElementType } from "react";
import {
  Button,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  ButtonProps,
} from "@chakra-ui/react";
import { FaWhatsapp } from "react-icons/fa";
import { Lead, Organization } from "../utils/types";

interface SalesWhatsAppButtonProps extends ButtonProps {
  lead: Lead;
  organization: Organization;
  iconOnly?: boolean;
  size?: "sm" | "md" | "lg";
  text?: string;
}

/**
 * Format phone number to WhatsApp compatible
 */
const formatWhatsAppNumber = (number: string) => {
  let digits = number.replace(/\D/g, "");
  if (digits.startsWith("0")) digits = digits.substring(1);
  if (digits.length < 9) digits = "263" + digits; // fallback country code
  return digits;
};

/**
 * Generates sales message templates using lead/org info
 */
const getTemplates = (lead: Lead, org: Organization) => [
  {
    id: 1,
    title: "Introduction / Follow-up",
    message: `Hi ${lead.first_name || lead.second_name || lead.email}, this is the team at ${org.name}. We wanted to follow up regarding your interest.`,
  },
  {
    id: 2,
    title: "Product Demo",
    message: `Hello ${lead.first_name || lead.second_name || lead.email}, we at ${org.name} would love to show you our product in action. Are you available for a demo?`,
  },
  {
    id: 3,
    title: "Special Offer",
    message: `Hi ${lead.first_name || lead.second_name || lead.email}, ${org.name} has a special offer for you. Let us know if you’re interested!`,
  },
  {
    id: 4,
    title: "Check-in",
    message: `Hello ${lead.first_name || lead.second_name || lead.email}, just checking in from ${org.name}. Do you have any questions or need further info?`,
  },
  {
    id: 5,
    title: "Proposal / Quote",
    message: `Hi ${lead.first_name || lead.second_name || lead.email}, attached is the proposal from ${org.name}. We’d love your feedback and to discuss next steps.`,
  },
  {
    id: 6,
    title: "Custom Message",
    message: "", // blank for user input
  },
];

export default function SalesWhatsAppButton({
  lead,
  organization,
  iconOnly = false,
  size = "md",
  text = "Send WhatsApp",
  ...props
}: SalesWhatsAppButtonProps) {
  const handleSend = (templateMessage: string) => {
    if (!lead.phone) return alert("Lead does not have a phone number");
    const formattedNumber = formatWhatsAppNumber(lead.phone);
    const url = `https://wa.me/${formattedNumber}?text=${encodeURIComponent(templateMessage)}`;
    // window.location.href = url;
    window.open(url, "_blank");
  };

  return (
    <Menu>
      <MenuButton
        as={Button}
        leftIcon={!iconOnly ? <Icon as={FaWhatsapp as ElementType} /> : undefined}
        size={size}
        colorScheme="green"
        bg={"#006400"}
        {...props}
      >
        {!iconOnly && text}
        {iconOnly && <Icon as={FaWhatsapp as ElementType} />}
      </MenuButton>
      <MenuList>
        {getTemplates(lead, organization).map((tpl) => (
          <MenuItem key={tpl.id} onClick={() => handleSend(tpl.message)}>
            <Text fontWeight="semibold">{tpl.title}</Text>
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
}
